import React, { useEffect } from 'react';
import { CardHeader, Card, CardContent, Box, Typography } from '@mui/material';
import { useStylesCustom } from '../DashboardCardItem';
import {
  useCancelledSubscriptionsMetricsLazyQuery,
  CancelledSubscriptionMetric,
} from '../../../../shared/types/generated';
import moment from 'moment';
import { Loading } from '../../../../shared/components/Loading';
import { CustomSizes } from '../../../../shared/components/loading-types';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import es from 'apexcharts/dist/locales/es.json';
import {
  CARD_HEIGHT_DASHBOARD,
  PROJECT_COLOR,
} from '../../../../shared/constants';
import { changeDashboardDateFilterStore } from '../../dashboard-events';
import { useStore } from '@cobuildlab/react-simple-state';
import { transformActivityRanges } from '../../../../shared/utils';

interface Props {
  compareWithPreviousPeriod: boolean;
}

export const DashboardSubscriptionCancelMetrics: React.FC<Props> = ({
  compareWithPreviousPeriod = false,
}) => {
  const { from, to, filter } = useStore(changeDashboardDateFilterStore);
  const classes = useStylesCustom();
  const [data, setData] = React.useState<{ x: string; y: number }[]>([]);
  const [previousPeriod, setPreviousPeriod] = React.useState<
    { x: string; y: number }[]
  >([]);

  const [callAction, { loading }] = useCancelledSubscriptionsMetricsLazyQuery({
    fetchPolicy: 'no-cache',
    onError: (error) => {
      console.error('Error fetching cancelled subscriptions metrics:', error);
    },
    onCompleted(event) {

      console.log('event', event);
      const session =
        (event?.cancelledSubscriptionsMetrics as CancelledSubscriptionMetric[]) ||
        [];

      if (session.length === 0) {
        setData([]);
        return;
      }

      const sessionNew = session
        .map((item) => ({
          x: item?.date
            ? moment(item?.date as string)
                .local()
                .format('YYYY-MM-DD HH:mm')
            : '',
          y: item?.count || 0,
        }))
        .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());

      const previousPeriodNew = session
        .map((item) => ({
          x: item?.date
            ? moment
                .utc(item?.date as string)
                .local()
                .format('YYYY-MM-DD HH:mm')
            : '',
          y: item?.previousCount || 0,
        }))
        .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());

      setData(sessionNew);
      setPreviousPeriod(previousPeriodNew);
    },
  });

  useEffect(() => {
    const range = transformActivityRanges(from, to, filter);
    console.log('range', range);

    callAction({
      variables: {
        where: {
          from: range.from,
          to: range.to,
        },
      },
    });
  }, [callAction, filter, from, to]);

  const DATA: {
    options: ApexOptions;
    series: ApexOptions['series'];
  } = {
    series: [
      {
        name: 'Suscripciones canceladas (periodo actual)',
        type: 'area',
        data: data || [],
      },
      ...(compareWithPreviousPeriod
        ? [
            {
              name: 'Suscripciones canceladas (periodo anterior)',
              type: 'line',
              data: previousPeriod || [],
            },
          ]
        : []),
    ],
    options: {
      colors: [PROJECT_COLOR, 'rgba(50,50,50,.85)'],
      chart: {
        id: 'user-registered-chart',
        height: 300,
        type: 'line',
        defaultLocale: 'es',
        locales: [es],
      },
      stroke: {
        width: [0, 3],
      },
      markers: {
        size: 0,
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        position: 'bottom',
        type: 'datetime',
        labels: {
          formatter(value) {
            const formattedDate = moment.utc(value as string).local();

            return formattedDate.format('DD/MM/YYYY hh:mm a');
          },
          offsetX: 10,
        },
        axisBorder: {
          show: false,
        },
        tickAmount: 4,
        axisTicks: {
          show: false,
        },
        crosshairs: {
          fill: {
            type: 'gradient',
            gradient: {
              colorFrom: '#D8E3F0',
              colorTo: '#BED1E6',
              stops: [0, 100],
              opacityFrom: 0.4,
              opacityTo: 0.5,
            },
          },
        },
        tooltip: {
          enabled: true,
          formatter: function (val: string) {
            return moment(val).format('DD-MM-YYYY hh:mm a');
          },
        },
      },
      yaxis: {
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          formatter: function (val) {
            return val.toLocaleString();
          },
        },
      },
    },
  };

  return (
    <>
      <Box
        marginY={2}
        sx={{
          display: { xs: 'block', md: 'flex' },
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Typography
          component={'div'}
          sx={{ marginBottom: { xs: '10px', md: '0px' } }}
          fontSize={20}
          fontWeight={700}>
          Suscripciones canceladas
        </Typography>
      </Box>
      <Card
        elevation={2}
        style={{
          border: '1px solid #BBBBBB',
          padding: '16px',
          borderRadius: '16px',
          boxShadow: '0px 0px 8px 0px #00000014',
        }}>
        <CardHeader
          title={'Suscripciones canceladas'}
          classes={{
            title: classes.title,
          }}
          className={classes.header}
        />
        <CardContent
          className={classes.content}
          style={{ height: CARD_HEIGHT_DASHBOARD }}>
          {loading ? (
            <Loading height="100%" customSize={CustomSizes.MEDIUM} />
          ) : (
            <Chart
              options={DATA.options}
              series={DATA.series}
              type="line"
              height={'100%'}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
};
